import { inject, Injectable } from '@angular/core';
import { ClaimFlagSettingsSchema } from '@libs/shared/domain/claim/flag/claim-flag-settings.schema';
import { InterpolationService } from '@services/interpolation.service';
import { LogService } from '@services/log.service';
import { PatientAssistanceRequestViewModel } from '@services/models/patient-assistance-request-view.model';
import { SettingsStore } from '@services/settings.store';
import { TenantService } from '@services/tenant.service';
import { UserService } from '@services/user.service';
import { FlagDiplayCategory, FlagsMap } from '@shared/components/claim-flags/claim-flags-types';
import { camelCase, chain, cloneDeep, concat, keyBy, maxBy, upperFirst } from 'lodash';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ClaimFlagsService {
  private readonly settingsStore = inject(SettingsStore);
  private readonly logService = inject(LogService);
  private readonly interpolationService = inject(InterpolationService);
  private readonly tenantService = inject(TenantService);
  private readonly userService = inject(UserService);

  public getAllFlagsSettings(options?: { includeInternal?: boolean; includeDisabled?: boolean }) {
    const tenants = this.tenantService
      .allTenants()
      .map(tenant => tenant.id)
      .filter(tenantId => this.userService.hasAllTenantsAccess() || this.userService.getUserTenant() === tenantId);

    return forkJoin(
      tenants.reduce(
        (acc, tenantId) => {
          acc[tenantId] = this.getFlagsSettings(tenantId, options);
          return acc;
        },
        {} as Record<string, Observable<ClaimFlagSettingsSchema<Record<string, any>, Record<string, any>>[]>>,
      ),
    );
  }

  public getFlagsSettings(tenantId: string, options?: { includeInternal?: boolean; includeDisabled?: boolean }) {
    const { includeInternal = false, includeDisabled = false } = options ?? {};
    return this.settingsStore.getSettings<ClaimFlagSettingsSchema[]>(`flags-${tenantId}`).pipe(
      map(data => {
        const items = data || [];
        return chain(items)
          .filter(flagSettings => {
            let include = true;
            if (!includeInternal) include = include && !flagSettings.is_internal;
            if (!includeDisabled) include = include && (flagSettings.enabled ?? true);
            return include;
          })
          .orderBy(['title'])
          .value();
      }),
    );
  }

  public getFlagsCategories() {
    return this.settingsStore.getSettings<FlagDiplayCategory[]>('flags', 'categories').pipe(map(data => data ?? []));
  }

  public getFlagsIdenity(map: FlagsMap) {
    let identity = 'Identified';
    const flags = concat(...Object.values(map));

    for (const flag of flags) {
      if (flag.actions.reject) {
        identity = 'Blocked';
        break;
      } else if (flag.actions.brightlink) {
        identity = 'brightLINK';
        break;
      }
    }

    return identity;
  }

  public getFlagsMap(claim?: PatientAssistanceRequestViewModel, flags?: ClaimFlagSettingsSchema[]): FlagsMap {
    const isCurrentUserSuperAdmin = UserService.isCurrentUserSuperAdmin();
    const flagsMap: FlagsMap = {};

    const flagsSettings = keyBy(flags ?? [], 'name');
    claim?.flagsList?.forEach(key => {
      const flag = cloneDeep(flagsSettings[key]);
      if (!isCurrentUserSuperAdmin && [undefined, 'Uncategorized'].includes(flag.category)) {
        return;
      }
      if (flag) {
        flag.title = this.interpolateFlagTitle(claim, flag.name, flag.title);
        flag.description = this.interpolateFlagDescription(claim, flag.name, flag.description);
        flag.amount_description = this.interpolateFlagDescription(claim, flag.name, flag.amount_description);
        if (flag.category) {
          flagsMap[flag.category] = flagsMap[flag.category] ?? [];
          flagsMap[flag.category].push(flag);
        } else {
          flagsMap.Uncategorized = flagsMap.Uncategorized ?? [];
          flagsMap.Uncategorized.push(flag);
        }
      } else {
        this.logService.message(`Unknown flag: ${key}`);
      }
    });

    return flagsMap;
  }

  public getFlagsValueDescription(claim: PatientAssistanceRequestViewModel, flagsMap: FlagsMap) {
    const flags = Object.values(flagsMap).flat();
    const highest = maxBy(Object.entries(claim.flagsAmounts), ([, value]) => value);
    const flag = highest ? flags.find(flag => flag.name === highest[0]) : undefined;
    return flag?.amount_description ?? '';
  }

  public interpolateFlagSettingTitle(flag: ClaimFlagSettingsSchema): string {
    let title = flag.title;

    for (const [key, value] of Object.entries(flag.params ?? {})) {
      title = title.replaceAll(`{params${upperFirst(camelCase(key))}}`, `${value}`);
    }

    return title;
  }

  private interpolateFlagDescription(claim: PatientAssistanceRequestViewModel, name: string, description?: string): string | undefined {
    if (!description) return undefined;
    const interpolationMap: Record<string, string> = {
      pharmacyOrFamilyName: claim.pharmacyName ?? `NCPDP ${claim?.pharmacyNcpdp} (unknown pharmacy/pharmacy family)`,
    };
    const prescriberNpi = claim.flagsContexts?.flag_prescriber_new?.claimPrescriberNpi;
    if (prescriberNpi) {
      const prescriberValue = claim.prescriberName ? `${claim.prescriberName} (${prescriberNpi})` : `${prescriberNpi} (Unknown Prescriber)`;
      description = description.replaceAll(`{claimPrescriberNpi}`, prescriberValue);
    }
    for (const interpolationKey of Object.keys(interpolationMap)) {
      description = description.replaceAll(`{${interpolationKey}}`, interpolationMap[interpolationKey]);
    }
    description = this.interpolationService.interpolateFromObject(description, claim.flagsContexts[name] ?? {});
    return this.interpolationService.interpolateFromObject(description, claim);
  }

  private interpolateFlagTitle(claim: PatientAssistanceRequestViewModel, name: string, title: string): string {
    return this.interpolationService.interpolateFromObject(title, claim.flagsContexts[name] ?? {});
  }
}
